import React, { useEffect, useState } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { getPerformanceResponses } from '../../../../services/performanceService';
import { jsPDF } from 'jspdf';
import { useAuth } from '../ServeyQuestions/AuthProvider'
import * as MicrosoftGraph from '@microsoft/microsoft-graph-client';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import html2canvas from 'html2canvas';
import './Calendar.css'; 
import { useTranslation } from 'react-i18next';

const localizer = momentLocalizer(moment);

const CalendarComponent = ({ userId }) => {
    const [events, setEvents] = useState([]);
    const [originalEvents, setOriginalEvents] = useState([]); 
    const [currentDate, setCurrentDate] = useState(new Date());
    const { pca, initialized } = useAuth(); // Get MSAL Auth
    const [loading, setLoading] = useState(false); 
    const { t } = useTranslation();

    const getColorByScore = (score) => {
        switch (true) {
            case score >= 1 && score <= 2:
                return 'lightblue';
            case score >= 3 && score <= 4:
                return 'blue';
            case score >= 5 && score <= 6:
                return 'darkblue';
            case score >= 7 && score <= 10:
                return 'green';
            default:
                return 'gray';
        }
    };

    const roundToNearestHour = (date) => {
        const rounded = new Date(date);
        rounded.setMinutes(0, 0, 0); 
        return rounded;
    };

    // Function to fetch the responses and set the events
    const fetchResponses = async () => {
        try {
            const response = await getPerformanceResponses(userId);
            console.log(response)
            const eventsData = createEventsFromData(response.data); 
            setEvents(eventsData);
            console.log(eventsData)
            setOriginalEvents(eventsData); 
        } catch (error) {
            console.error('Error fetching performance responses:', error);
        }
    };

    const calculateHourlyAverages = (data) => {
        const scoresByHour = {};
        
        data.forEach(item => {
            const timestamp = new Date(item.timestamp);
            const hour = timestamp.getHours();
            
            if (!scoresByHour[hour]) {
                scoresByHour[hour] = [];
            }
            scoresByHour[hour].push(item.score);
        });
        
        const averagesByHour = {};
        Object.keys(scoresByHour).forEach(hour => {
            const scores = scoresByHour[hour];
            const sum = scores.reduce((acc, score) => acc + score, 0);
            console.log(`Hour: ${hour}, Scores: ${scores}, Sum: ${sum}`);
            averagesByHour[hour] = Math.round(sum / scores.length);
            console.log(`Average for hour ${hour}: ${averagesByHour[hour]}`);
        });
        
        return averagesByHour;
    };

    const createCurrentDayEventsFromAverages = (averagesByHour) => {
        const eventsData = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        Object.entries(averagesByHour).forEach(([hour, averageScore]) => {
            const start = new Date(today);
            start.setHours(parseInt(hour), 0, 0, 0);
            
            const end = new Date(start.getTime() + 3600000);
            const color = getColorByScore(averageScore);
            
            const event = {
                id: `avg-${hour}`,
                title: `Score: ${averageScore}`,
                start,
                end,
                allDay: false,
                backgroundColor: color,
                style: {
                    backgroundColor: color,
                    borderColor: color,
                    color: 'white',
                },
                score: averageScore,
                isAveraged: true
            };
            
            eventsData.push(event);
        });
        
        return eventsData;
    };
        //Falback ICS export function
        // 1. Function to generate ICS content
        const generateICSContent = (events) => {
            let icsContent = [
                'BEGIN:VCALENDAR',
                'VERSION:2.0',
                'PRODID:-//Performance Score Calendar//EN',
                'CALSCALE:GREGORIAN',
                'METHOD:PUBLISH'
            ];
    
            events.forEach(event => {
                icsContent = icsContent.concat([
                    'BEGIN:VEVENT',
                    `UID:${event.id}@performancescore`,
                    `DTSTAMP:${moment().format('YYYYMMDDTHHmmss')}Z`,
                    `DTSTART:${moment(event.start).format('YYYYMMDDTHHmmss')}Z`,
                    `DTEND:${moment(event.end).format('YYYYMMDDTHHmmss')}Z`,
                    `SUMMARY:Score: ${event.score}${event.isAveraged ? ' (Averaged)' : ''}`,
                    `DESCRIPTION:Performance Score: ${event.score}`,
                    'END:VEVENT'
                ]);
            });
    
            icsContent.push('END:VCALENDAR');
            return icsContent.join('\r\n');
        };
    // 2. Function to download ICS file
    const downloadICSFile = async () => {
        try {
            setLoading(true);
            
            const response = await getPerformanceResponses(userId);
            const averagesByHour = calculateHourlyAverages(response.data);
            const eventsData = createCurrentDayEventsFromAverages(averagesByHour);
            
            const icsContent = generateICSContent(eventsData);
            
            const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = 'performance_scores_averages.ics';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            alert('Calendar file with averaged scores downloaded successfully!');
        } catch (error) {
            console.error('Error generating ICS file:', error);
            alert('Failed to generate calendar file.');
        } finally {
            setLoading(false);
        }
    };
    // 3. Function to show export options
    const handleExport = () => {
        const exportOptions = [
            { id: 'outlook', label: t('calendar.exportToOutlook', 'Export to Outlook') },
            { id: 'ics', label: t('calendar.downloadICSFile', 'Download Calendar File') }
        ];
        
        const modal = document.createElement('div');
        modal.style.position = 'fixed';
        modal.style.top = '0';
        modal.style.left = '0';
        modal.style.width = '100%';
        modal.style.height = '100%';
        modal.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
        modal.style.display = 'flex';
        modal.style.justifyContent = 'center';
        modal.style.alignItems = 'center';
        modal.style.zIndex = '1000';
        
        const modalContent = document.createElement('div');
        modalContent.style.backgroundColor = 'white';
        modalContent.style.padding = '20px';
        modalContent.style.borderRadius = '5px';
        modalContent.style.maxWidth = '400px';
        modalContent.style.width = '100%';
        
        const title = document.createElement('h3');
        title.textContent = t('calendar.exportOptions', 'Export Options');
        modalContent.appendChild(title);
        
        exportOptions.forEach(option => {
            const button = document.createElement('button');
            button.textContent = option.label;
            button.style.display = 'block';
            button.style.width = '100%';
            button.style.padding = '10px';
            button.style.margin = '10px 0';
            button.style.cursor = 'pointer';
            
            button.onclick = () => {
                document.body.removeChild(modal);
                if (option.id === 'outlook') {
                    handleICSExport();
                } else {
                    downloadICSFile();
                }
            };
            
            modalContent.appendChild(button);
        });
        
        const cancelButton = document.createElement('button');
        cancelButton.textContent = t('common.cancel', 'Cancel');
        cancelButton.style.display = 'block';
        cancelButton.style.width = '100%';
        cancelButton.style.padding = '10px';
        cancelButton.style.margin = '10px 0';
        cancelButton.style.cursor = 'pointer';
        
        cancelButton.onclick = () => {
            document.body.removeChild(modal);
        };
        
        modalContent.appendChild(cancelButton);
        modal.appendChild(modalContent);
        document.body.appendChild(modal);
    };

  

    const getCategoryByScore = (score) => {
        if (score >= 1 && score <= 2) return 'Low Score';
        if (score >= 3 && score <= 4) return 'Moderate Score';
        if (score >= 5 && score <= 6) return 'High Score';
        if (score >= 7 && score <= 10) return 'Very High Score';
        return 'Uncategorized';
    };

    const createEvent = async (client, event) => {
        const category = getCategoryByScore(event.score);
        const outlookEvent = {
            subject: `Score: ${event.score}`,
            start: { dateTime: event.start.toISOString(), timeZone: 'UTC' },
            end: { dateTime: event.end.toISOString(), timeZone: 'UTC' },
            body: { contentType: 'HTML', content: `Performance Score: ${event.score}` },
            categories: [category]
        };

        try {
            await client.api('/me/events').post(outlookEvent);
        } catch (error) {
            console.error('Error creating event:', error);
        }
    };

    const defineCategories = async (client) => {
        const categories = [
            { displayName: 'Low Score', color: 'preset7' },   // Blue
            { displayName: 'Moderate Score', color: 'preset4' }, // Green
            { displayName: 'High Score', color: 'preset1' },   // Orange
            { displayName: 'Very High Score', color: 'preset15' } // DarkRed
        ];

        for (const category of categories) {
            try {
                await client.api('/me/outlook/masterCategories')
                    .post(category);
            } catch (error) {
                if (error.statusCode === 409) {
                    console.log(`Category '${category.displayName}' already exists.`);
                } else {
                    console.error(`Error creating category '${category.displayName}':`, error);
                }
            }
        }
    };

    const handleICSExport = async () => {
        if (!initialized) {
            console.error('MSAL not initialized');
            return;
        }

        setLoading(true);

        try {
            const accounts = pca.getAllAccounts();
            if (accounts.length === 0) {
                await pca.loginPopup();
            }
            const account = pca.getAllAccounts()[0];

            let tokenResponse;
            try {
                tokenResponse = await pca.acquireTokenSilent({
                    scopes: ["Calendars.ReadWrite", "User.Read"],
                    account: account
                });
            } catch (error) {
                if (error instanceof InteractionRequiredAuthError) {
                    tokenResponse = await pca.acquireTokenPopup({
                        scopes: ["Calendars.ReadWrite", "User.Read"],
                        account: account
                    });
                } else {
                    throw error;
                }
            }

            const client = MicrosoftGraph.Client.init({
                authProvider: (done) => {
                    done(null, tokenResponse.accessToken);
                }
            });

            await defineCategories(client);

            const calendars = await client.api('/me/calendars').get();
            let calendarId = null;

            for (const calendar of calendars.value) {
                if (calendar.name === 'Survey Notifications') {
                    calendarId = calendar.id;
                    break;
                }
            }

            if (!calendarId) {
                const newCalendar = await client.api('/me/calendars').post({
                    name: 'Survey Notifications'
                });
                calendarId = newCalendar.id;
            }

            const response = await getPerformanceResponses(userId);
            const averagesByHour = calculateHourlyAverages(response.data);
            const eventsData = createCurrentDayEventsFromAverages(averagesByHour);

            for (const event of eventsData) {
                const category = getCategoryByScore(event.score);
                const outlookEvent = {
                    subject: `Score: ${event.score} (Average)`,
                    start: { dateTime: event.start.toISOString(), timeZone: 'UTC' },
                    end: { dateTime: event.end.toISOString(), timeZone: 'UTC' },
                    body: { contentType: 'HTML', content: `Performance Score: ${event.score} (Historical Average)` },
                    categories: [category]
                };

                await client.api(`/me/calendars/${calendarId}/events`).post(outlookEvent);
            }

            alert('Averaged events added to your Outlook Calendar for today!');
        } catch (error) {
            console.error('Error exporting to Outlook calendar:', error);
            
            if (error.message && (
                error.message.includes('AADSTS50020') ||
                error.message.includes('AADSTS90072') ||
                error.message.includes('AADSTS700016') ||
                error.message.includes('selected account is not in my working company')
            )) {
                const userConfirmed = window.confirm('Your Microsoft account cannot be used with this application. Would you like to download a calendar file instead?');
                if (userConfirmed) {
                    downloadICSFile();
                }
            } else {
                alert('Failed to add events to Outlook Calendar.');
            }
        } finally {
            setLoading(false);
        }
    };

    const createEventsFromData = (data) => {
        const eventsData = [];
        data.forEach((item, index) => {
            const start = roundToNearestHour(new Date(item.timestamp));
            const color = getColorByScore(item.score);

            const event = {
                id: String(index),
                title: `Score: ${item.score}`,
                start,
                end: new Date(start.getTime() + 3600000), // 1 hour event
                allDay: false,
                backgroundColor: color,
                style: {
                    backgroundColor: color,
                    borderColor: color,
                    color: 'white', // Text color for better readability
                },
                score: item.score,
            };

            eventsData.push(event);
        });

        return eventsData;
    };

    useEffect(() => {
        fetchResponses();
    }, [userId]);

    return (
        <div className="calendar-container">
            <div className="legend-card">
                <h3>{t('calendar.scoreLegend')}</h3>
                <ul>
                    <li><span className="legend-color" style={{ backgroundColor: 'lightblue' }}></span> {t('calendar.score1_2')}</li>
                    <li><span className="legend-color" style={{ backgroundColor: 'blue' }}></span> {t('calendar.score3_4')}</li>
                    <li><span className="legend-color" style={{ backgroundColor: 'darkblue' }}></span> {t('calendar.score5_6')}</li>
                    <li><span className="legend-color" style={{ backgroundColor: 'green' }}></span> {t('calendar.score7_10')}</li>
                    <li><span className="legend-color" style={{ backgroundColor: 'gray' }}></span> {t('calendar.noScore')}</li>
                </ul>
            </div>
            {/* Change this line to use handleExport instead of handleICSExport */}
            <button onClick={handleExport} className="export-button">{t('calendar.export', 'Export')}</button>
            <div style={{ height: 'calc(100vh - 100px)', width: '100%' }}>
                <Calendar
                    localizer={localizer}
                    events={events}
                    startAccessor="start"
                    endAccessor="end"
                    style={{ height: '100%', width: '100%' }}
                    eventPropGetter={(event) => ({
                        style: event.style,
                    })}
                    views={['week']}
                    defaultView="week"
                    date={currentDate}
                    onNavigate={date => setCurrentDate(date)}
                    min={new Date(1970, 1, 1, 8, 0, 0)}
                    max={new Date(1970, 1, 1, 19, 0, 0)}
                />
            </div>
            {/* Add loading indicator */}
            {loading && (
                <div className="loading-overlay">
                    <div className="loading-spinner"></div>
                    <p>{t('common.loading', 'Loading...')}</p>
                </div>
            )}
        </div>
    );
};

export default CalendarComponent;
